window.ThemeCart = window.ThemeCart || {};

class ThemeMainCartRenderer {
  static sectionHost() {
    return document
      .querySelector('[data-section-type="main-cart"]')
      ?.closest('[id^="shopify-section-"]') || null;
  }

  static sectionUrl(sectionId) {
    const rootUrl = window.routes?.localeRootUrl || "";
    return `${rootUrl}/cart?section_id=${encodeURIComponent(sectionId)}&t=${Date.now()}`;
  }

  /**
   * Update cart-drawer__footer children but keep .main-cart__usp in the DOM.
   * @param {HTMLElement | null} oldFooter
   * @param {HTMLElement | null} newFooter
   */
  static patchFooterPreservingUsp(oldFooter, newFooter) {
    if (!newFooter) {
      oldFooter?.remove();
      return;
    }
    if (!oldFooter) return;

    const usp = oldFooter.querySelector(".main-cart__usp");
    if (!usp) {
      oldFooter.replaceWith(newFooter.cloneNode(true));
      return;
    }

    Array.from(oldFooter.children).forEach((child) => {
      if (child === usp) return;
      child.remove();
    });

    const newFooterClone = newFooter.cloneNode(true);
    newFooterClone.querySelector(".main-cart__usp")?.remove();
    Array.from(newFooterClone.children).forEach((child) => {
      oldFooter.insertBefore(child, usp);
    });
  }

  /**
   * @param {HTMLElement | null} oldWrapper
   * @param {HTMLElement | null} newWrapper
   */
  static patchShippingFooterWrapper(oldWrapper, newWrapper) {
    if (!newWrapper) {
      oldWrapper?.remove();
      return;
    }
    if (!oldWrapper) return;

    const oldBar = oldWrapper.querySelector(
      ".cart-drawer__shipping-bar-wrapper.not_mobile.not_pocket",
    );
    const newBar = newWrapper.querySelector(
      ".cart-drawer__shipping-bar-wrapper.not_mobile.not_pocket",
    );
    if (oldBar && newBar) {
      oldBar.replaceWith(newBar.cloneNode(true));
    } else if (newBar && !oldBar) {
      oldWrapper.prepend(newBar.cloneNode(true));
    } else if (oldBar && !newBar) {
      oldBar.remove();
    }

    ThemeMainCartRenderer.patchFooterPreservingUsp(
      oldWrapper.querySelector(".cart-drawer__footer"),
      newWrapper.querySelector(".cart-drawer__footer"),
    );
  }

  /**
   * Partial cart-page refresh: patch lines + totals, never replace .main-cart__usp.
   * @param {HTMLElement} sectionHost
   * @param {string} html
   * @returns {boolean}
   */
  static patchFromSectionHtml(sectionHost, html) {
    const temp = document.createElement("div");
    temp.innerHTML = html;
    const newMainCart = temp.querySelector("main-cart");
    const oldMainCart = sectionHost.querySelector("main-cart");
    if (!newMainCart || !oldMainCart) return false;

    const oldWrapper = oldMainCart.querySelector(".main-cart__wrapper");
    const newWrapper = newMainCart.querySelector(".main-cart__wrapper");
    if (!oldWrapper || !newWrapper) return false;

    const wasEmpty = oldWrapper.classList.contains("main-cart__wrapper--empty");
    const isEmpty = newWrapper.classList.contains("main-cart__wrapper--empty");
    if (wasEmpty !== isEmpty) return false;

    oldWrapper.className = newWrapper.className;

    const productId = newMainCart.getAttribute("data-product-id");
    if (productId) oldMainCart.setAttribute("data-product-id", productId);
    else oldMainCart.removeAttribute("data-product-id");

    const settingsJson = newMainCart.getAttribute("data-settings");
    if (settingsJson) oldMainCart.setAttribute("data-settings", settingsJson);
    else oldMainCart.removeAttribute("data-settings");

    const oldHeader = oldMainCart.querySelector(".main-cart__header");
    const newHeader = newMainCart.querySelector(".main-cart__header");
    if (newHeader) {
      if (oldHeader) oldHeader.replaceWith(newHeader.cloneNode(true));
      else oldWrapper.insertBefore(newHeader.cloneNode(true), oldWrapper.firstChild);
    } else {
      oldHeader?.remove();
    }

    const oldContent = oldMainCart.querySelector(".main-cart__content");
    const newContent = newMainCart.querySelector(".main-cart__content");
    if (!oldContent || !newContent) return false;

    const oldFull = oldContent.classList.contains("main-cart__content--full");
    const newFull = newContent.classList.contains("main-cart__content--full");
    if (oldFull !== newFull) return false;

    if (!oldFull) {
      oldContent.replaceWith(newContent.cloneNode(true));
      return true;
    }

    const oldMobileBar = oldContent.querySelector(
      ".cart-drawer__shipping-bar-wrapper.not_desktop",
    );
    const newMobileBar = newContent.querySelector(
      ".cart-drawer__shipping-bar-wrapper.not_desktop",
    );
    if (oldMobileBar && newMobileBar) {
      oldMobileBar.replaceWith(newMobileBar.cloneNode(true));
    } else if (newMobileBar && !oldMobileBar) {
      oldContent.prepend(newMobileBar.cloneNode(true));
    } else if (oldMobileBar && !newMobileBar) {
      oldMobileBar.remove();
    }

    const oldForm = oldContent.querySelector("#main-cart__form");
    const newForm = newContent.querySelector("#main-cart__form");
    if (!oldForm || !newForm) return false;
    oldForm.replaceWith(newForm.cloneNode(true));

    ThemeMainCartRenderer.patchShippingFooterWrapper(
      oldContent.querySelector(".main-cart__shipping-footer--wrapper"),
      newContent.querySelector(".main-cart__shipping-footer--wrapper"),
    );

    return true;
  }

  static replaceSectionFromHtml(sectionHost, html) {
    const temp = document.createElement("div");
    temp.innerHTML = html;
    const newSection = temp.querySelector("section.main-cart__outer-wrapper");
    const oldSection = sectionHost.querySelector("section.main-cart__outer-wrapper");
    if (newSection && oldSection) {
      oldSection.replaceWith(newSection);
    } else {
      sectionHost.innerHTML = html;
    }
  }

  static async refreshSection() {
    const sectionHost = ThemeMainCartRenderer.sectionHost();
    if (!sectionHost) return false;

    const sectionId = sectionHost.id.replace("shopify-section-", "");
    const res = await fetch(ThemeMainCartRenderer.sectionUrl(sectionId), {
      credentials: "same-origin",
      method: "GET",
      cache: "no-store",
    });
    if (!res.ok) return false;

    const html = await res.text();
    const patched = ThemeMainCartRenderer.patchFromSectionHtml(sectionHost, html);

    if (!patched) {
      ThemeMainCartRenderer.replaceSectionFromHtml(sectionHost, html);
    }

    document.dispatchEvent(new CustomEvent("cart:render", { bubbles: true }));
    return true;
  }
}

window.ThemeCart.MainCartRenderer = ThemeMainCartRenderer;
